import React, { Suspense, lazy, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Chip,
  Skeleton,
  Stack,
  Typography,
  useTokens,
} from '@barracuda-internal/bds-core';
import {
  Apartment,
  Block,
  Code,
  Earth,
  ErrorOutline,
  Lock,
  NotificationWarning,
  Schedule,
  ShieldLock,
  Sync,
} from '@barracuda-internal/bds-core/dist/Icons/Core';
import GroupedKpiCard from './GroupedKpiCard';
import MiniStatusChart from './MiniStatusChart';
import TrendStrip from './TrendStrip';
import RecentScansTable from './RecentScansTable';

// Globe + three.js are heavy; defer their chunk until the dashboard
// actually mounts, instead of paying the cost on every route.
const GeoLocationSection = lazy(() => import('./GeoLocationSection'));

const PERIODS = [
  { days: 1, label: '24h' },
  { days: 7, label: '7d' },
  { days: 30, label: '30d' },
];

const formatCount = (n) => Number(n || 0).toLocaleString();

const formatLatency = (ms) => {
  if (!ms) return '0 ms';
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)} s`;
  return `${ms} ms`;
};

const computeDelta = (current, previous) => {
  if (previous === 0) {
    if (!current) return 0;
    return 100;
  }
  return Math.round(((current - previous) / previous) * 1000) / 10;
};

const PeriodToggle = ({ period, onChange }) => {
  const tokens = useTokens();
  return (
    <Stack
      direction="row"
      sx={{
        gap: tokens.aliasesSpaceComponentDense,
        alignItems: 'center',
      }}
    >
      {PERIODS.map(({ days, label }) => (
        <Chip
          key={days}
          bdsType={period === days ? 'interactiveEmphasis' : 'interactiveSubtle'}
          bdsSize="medium"
          clickable
          selected={period === days}
          onClick={() => onChange(days)}
          label={label}
        />
      ))}
    </Stack>
  );
};

const DashboardSkeleton = () => {
  const tokens = useTokens();
  return (
    <Stack sx={{ gap: tokens.aliasesSpaceContainerSpacious }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Skeleton variant="text" width={160} height={40} />
        <Skeleton variant="rectangular" width={220} height={36} />
      </Stack>
      <Skeleton variant="rectangular" height={220} />
      <Skeleton variant="rectangular" height={420} />
      <Skeleton variant="rectangular" height={220} />
    </Stack>
  );
};

const DashboardComponent = ({
  timeseries,
  period,
  loading,
  timeseriesLoading,
  onPeriodChange,
}) => {
  const tokens = useTokens();
  const breakdowns = useSelector((s) => s.guardian.dashboard.breakdowns);

  const totals = timeseries?.totals || {};
  const prev = timeseries?.previous_totals || {};
  const buckets = timeseries?.buckets || [];
  const byThreat = timeseries?.by_threat_type || {
    pii: 0,
    prompt_injection: 0,
    both: 0,
  };
  const byContent = timeseries?.by_content_type || [];
  const regions = breakdowns?.by_region || [];
  const modeBuckets = breakdowns?.processing_mode_buckets || [];
  const modeTotals = breakdowns?.processing_mode_totals || { sync: 0, async: 0 };

  // Derive admin-style series from the account timeseries shape
  // (clean / pii / prompt_injection / both / failed / processing per bucket).
  const scansSeries = useMemo(
    () =>
      buckets.map(
        (b) =>
          (b.clean || 0)
          + (b.pii || 0)
          + (b.prompt_injection || 0)
          + (b.both || 0)
          + (b.failed || 0)
          + (b.processing || 0)
      ),
    [buckets]
  );
  const threatsSeries = useMemo(
    () => buckets.map((b) => (b.pii || 0) + (b.prompt_injection || 0) + (b.both || 0)),
    [buckets]
  );
  const failedSeries = useMemo(() => buckets.map((b) => b.failed || 0), [buckets]);
  const modeSeries = useMemo(
    () => modeBuckets.map((b) => (b.sync || 0) + (b.async || 0)),
    [modeBuckets]
  );

  if ((loading && !timeseries) || (timeseriesLoading && !timeseries)) {
    return <DashboardSkeleton />;
  }

  const threatTotal =
    (byThreat.pii || 0) + (byThreat.prompt_injection || 0) + (byThreat.both || 0);
  const prevThreatTotal = prev.blocked || 0;
  const blockRate = totals.block_rate || 0;
  const prevBlockRate = prev.block_rate || 0;
  const failureRate = totals.total_scans
    ? Math.round(((totals.failed || 0) / totals.total_scans) * 1000) / 10
    : 0;
  const prevFailureRate = prev.total_scans
    ? Math.round(((prev.failed || 0) / prev.total_scans) * 1000) / 10
    : 0;
  const asyncTot = (modeTotals.sync || 0) + (modeTotals.async || 0);
  const asyncPct = asyncTot
    ? Math.round(((modeTotals.async || 0) / asyncTot) * 1000) / 10
    : 0;

  // Group A — top-line activity & threats (4 tiles)
  const tilesA = [
    {
      label: 'Total scans',
      value: formatCount(totals.total_scans),
      icon: <Schedule />,
      delta: {
        value: computeDelta(totals.total_scans, prev.total_scans || 0),
        positiveIsGood: true,
      },
    },
    {
      label: 'Block rate',
      value: `${blockRate}%`,
      icon: <Block />,
      delta: {
        value: computeDelta(blockRate, prevBlockRate),
        positiveIsGood: false,
      },
    },
    {
      label: 'Threats blocked',
      value: formatCount(threatTotal),
      icon: <ShieldLock />,
      accent: tokens.chartsColorSeries5Range100,
      delta: {
        value: computeDelta(threatTotal, prevThreatTotal),
        positiveIsGood: false,
      },
    },
    {
      label: 'Failed scans',
      value: formatCount(totals.failed),
      icon: <ErrorOutline />,
      delta: {
        value: computeDelta(totals.failed, prev.failed || 0),
        positiveIsGood: false,
      },
    },
  ];

  // Group B — health (2 tiles)
  const tilesB = [
    {
      label: 'Avg latency',
      value: formatLatency(totals.avg_latency_ms),
      icon: <Schedule />,
      delta: {
        value: computeDelta(totals.avg_latency_ms, prev.avg_latency_ms || 0),
        positiveIsGood: false,
      },
    },
    {
      label: 'Failure rate',
      value: `${failureRate}%`,
      icon: <NotificationWarning />,
      delta: {
        value: computeDelta(failureRate, prevFailureRate),
        positiveIsGood: false,
      },
    },
  ];

  // Group C — threat breakdown (2 tiles)
  const tilesC = [
    {
      label: 'PII detections',
      value: formatCount(byThreat.pii),
      icon: <Lock />,
      accent: tokens.chartsColorSeries6Range100,
    },
    {
      label: 'Prompt injections',
      value: formatCount(byThreat.prompt_injection),
      icon: <Code />,
      accent: tokens.chartsColorSeries5Range100,
    },
  ];

  // Group D — context (2 tiles)
  const tilesD = [
    {
      label: 'Multi-threat',
      value: formatCount(byThreat.both),
      icon: <NotificationWarning />,
      accent: tokens.chartsColorSeries5Range100,
    },
    {
      label: 'Top region',
      value: regions[0]?.region || '—',
      icon: <Earth />,
      accent: tokens.chartsColorSeries2Range100,
    },
  ];

  // Group E — patterns (2 tiles)
  const tilesE = [
    {
      label: 'Async share',
      value: `${asyncPct}%`,
      icon: <Sync />,
    },
    {
      label: 'Top content type',
      value: byContent[0]?.name?.split('/').pop() || '—',
      icon: <Apartment />,
    },
  ];

  return (
    <Stack sx={{ gap: tokens.aliasesSpaceContainerSpacious }}>
      {/* Header */}
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: tokens.aliasesSpaceComponentDefault,
        }}
      >
        <Stack sx={{ gap: tokens.aliasesSpaceComponentDense }}>
          <Typography variant="h2">Dashboard</Typography>
          <Typography variant="captionSubtle">
            Account activity, threats, and health
          </Typography>
        </Stack>
        <Stack
          direction="row"
          sx={{
            gap: tokens.aliasesSpaceComponentDefault,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <PeriodToggle period={period} onChange={onPeriodChange} />
        </Stack>
      </Stack>

      {/* KPI strip — 2 rows on the left (4+2 / 2+2+2 tiles) + tall sidekick on the right */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'repeat(6, 1fr) minmax(220px, 1.4fr)',
          },
          gridAutoRows: 'auto',
          gap: tokens.aliasesSpaceContainerDefault,
          alignItems: 'stretch',
        }}
      >
        {/* Row 1: Group A (cols 1-4) | Group B (cols 5-6) */}
        <Box sx={{ gridColumn: { lg: '1 / 5' } }}>
          <GroupedKpiCard tiles={tilesA} />
        </Box>
        <Box sx={{ gridColumn: { lg: '5 / 7' } }}>
          <GroupedKpiCard tiles={tilesB} />
        </Box>

        {/* Row 2: Group C | Group D | Group E */}
        <Box sx={{ gridColumn: { lg: '1 / 3' } }}>
          <GroupedKpiCard tiles={tilesC} />
        </Box>
        <Box sx={{ gridColumn: { lg: '3 / 5' } }}>
          <GroupedKpiCard tiles={tilesD} />
        </Box>
        <Box sx={{ gridColumn: { lg: '5 / 7' } }}>
          <GroupedKpiCard tiles={tilesE} />
        </Box>

        {/* Right sidekick: col 7, spans both rows. AREA. */}
        <Box
          sx={{
            gridColumn: { lg: '7 / 8' },
            gridRow: { lg: '1 / 3' },
            minWidth: 0,
          }}
        >
          <MiniStatusChart
            title="Scans / time"
            data={scansSeries}
            color={tokens.chartsColorSeries2Range100}
            fill={tokens.chartsColorSeries2Range20}
            height="auto"
            area
          />
        </Box>
      </Box>

      {/* Geo Location row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '3fr 1fr' },
          gap: tokens.aliasesSpaceContainerDefault,
          alignItems: 'stretch',
        }}
      >
        <Suspense fallback={<Skeleton variant="rectangular" height={400} />}>
          <GeoLocationSection
            items={regions}
            subtitle="Where this account's traffic originates from"
          />
        </Suspense>
        <Stack
          sx={{
            gap: tokens.aliasesSpaceContainerDefault,
            minWidth: 0,
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <MiniStatusChart
              title="Requests status"
              data={scansSeries}
              color={tokens.chartsColorSeries2Range100}
              height="auto"
              area={false}
            />
          </Box>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <MiniStatusChart
              title="Blocking status"
              data={threatsSeries}
              color={tokens.chartsColorSeries5Range100}
              height="auto"
              area={false}
            />
          </Box>
        </Stack>
      </Box>

      {/* Trend strips — mix area/line for visual rhythm */}
      <Stack sx={{ gap: tokens.aliasesSpaceContainerDefault }}>
        <TrendStrip
          title="Scan activity"
          data={scansSeries}
          total={totals.total_scans}
          color={tokens.chartsColorSeries2Range100}
          fill={tokens.chartsColorSeries2Range20}
          area
          items={byContent.slice(0, 5).map((c) => ({
            key: c.name,
            value: c.count,
          }))}
          itemsTitle="Top content types"
        />
        <TrendStrip
          title="Threats blocked"
          data={threatsSeries}
          total={threatTotal}
          color={tokens.chartsColorSeries5Range100}
          area={false}
          items={[
            { key: 'PII detections', value: byThreat.pii || 0 },
            { key: 'Prompt injections', value: byThreat.prompt_injection || 0 },
            { key: 'Multi-threat', value: byThreat.both || 0 },
          ]}
          itemsTitle="By type"
        />
        <TrendStrip
          title="Failed scans"
          data={failedSeries}
          total={totals.failed}
          color={tokens.aliasesColorTextStaticSubtle}
          area={false}
          items={[
            { key: 'Failure rate', value: `${failureRate}%` },
            { key: 'Total scans', value: totals.total_scans || 0 },
            { key: 'Avg latency', value: formatLatency(totals.avg_latency_ms) },
          ]}
          itemsTitle="Summary"
        />
        <TrendStrip
          title="Processing mode"
          data={modeSeries}
          total={asyncTot}
          color={tokens.chartsColorSeries1Range100}
          fill={tokens.chartsColorSeries1Range20}
          area
          items={[
            { key: 'Sync', value: modeTotals.sync || 0 },
            { key: 'Async', value: modeTotals.async || 0 },
            { key: 'Async share', value: `${asyncPct}%` },
          ]}
          itemsTitle="Mix"
        />
      </Stack>

      {/* Recent scans — account dashboard only (admin doesn't have this) */}
      <RecentScansTable />
    </Stack>
  );
};

export default DashboardComponent;
